import React from "react";
import { Progress } from "reactstrap";
import resStyles from "../css/scanningSessionResponsive.module.scss";

interface MobileHeaderProps {
  sessionData: any;
  activeTab: "select" | "step1" | "step2";
  toggleTab: (tab: "select" | "step1" | "step2") => void;
  handleUpdateStatus: (
    status: "paused" | "completed" | "active",
    shouldNavigate?: boolean
  ) => void;
  setShowProgressModal: (show: boolean) => void;
  completedSubsCount?: number;
  inProgressSubsCount?: number;
  onBackClick?: () => void;
}

const MobileScanningHeader: React.FC<MobileHeaderProps> = ({
  sessionData,
  activeTab,
  toggleTab,
  handleUpdateStatus,
  setShowProgressModal,
  completedSubsCount,
  onBackClick,
}) => {
  const isSeparation = activeTab === "step2" || sessionData?.sessionactivity === "sample_separation";
  const skippedSubs = sessionData?.skippedsubjects ?? sessionData?.skipped_subjects ?? 0;
  const totalSubs = sessionData?.totalsubjects ?? sessionData?.total_subjects ?? 0;
  const rawCompleted = completedSubsCount !== undefined
    ? completedSubsCount
    : isSeparation
      ? Math.floor((sessionData?.completedaliquots ?? 0) / (sessionData?.aliquotspersubject || 1))
      : ((sessionData?.completedsubjects ?? sessionData?.completed_subjects ?? 0) + skippedSubs);
  const completedSubs = Math.min(rawCompleted, totalSubs);

  return (
    <div className={resStyles.compactHeaderSection}>
      <div className={resStyles.headerTopRow}>
        <div className={resStyles.compactHeaderLeft}>
          <button
            type="button"
            className={resStyles.compactBackBtn}
            onClick={() => {
              if (onBackClick) {
                onBackClick();
              } else {
                handleUpdateStatus("paused", true);
              }
            }}
          >
            <i className="ri-arrow-left-line"></i>
          </button>
          <div className="d-flex align-items-center gap-2">
            <span className="d-block text-dark fw-bold" style={{ fontSize: "14px" }}>Active Session</span>
            <span className="badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25 ms-2">
              {sessionData?.sessionno || sessionData?.sessionid || "-"}
            </span>
          </div>
        </div>

        <button
          type="button"
          className={resStyles.compactProgressBtn}
          onClick={() => setShowProgressModal(true)}
          title="View Detailed Progress"
        >
          <div className={resStyles.progressBtnLeft}>
            <span className={resStyles.progressBtnVal}>
              {completedSubs}/
              {totalSubs}
            </span>
            <span className={resStyles.progressBtnLabel}>Subs</span>
          </div>
          <div className={resStyles.progressBtnRight}>
            <i className="ri-pie-chart-2-line"></i>
          </div>
        </button>
      </div>

      <Progress className={resStyles.compactProgressBar} value={(completedSubs / (totalSubs)) * 100} color="success" />

      <div className={resStyles.compactMetadataGrid}>
        <div className={resStyles.metaPairRow}>
          <div className={resStyles.metaItem}>
            <span className={resStyles.metaLabel}>Study:</span>
            <strong className={resStyles.metaValue}>{sessionData?.studycode || "-"}</strong>
          </div>
          <span className={resStyles.desktopDivider}>|</span>
          <div className={resStyles.metaItem}>
            <span className={resStyles.metaLabel}>Period:</span>
            <strong className={resStyles.metaValue}>{sessionData?.period_number || "-"}</strong>
          </div>
        </div>

        <span className={resStyles.desktopDivider}>|</span>

        <div className={resStyles.metaPairRow}>
          <div className={resStyles.metaItem}>
            <span className={resStyles.metaLabel}>Time Point:</span>
            <strong className={resStyles.metaValue}>{sessionData?.timepoint_number || "-"}</strong>
          </div>
          {sessionData?.dosingstationno != null && (
            <>
              <span className={resStyles.desktopDivider}>|</span>
              <div className={resStyles.metaItem}>
                <span className={resStyles.metaLabel}>Dosing Station:</span>
                <strong className={`${resStyles.metaValue} text-primary`}>
                  {`DS-${String(sessionData.dosingstationno).padStart(2, "0")}`}
                </strong>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MobileScanningHeader;
